import { Component, inject, OnInit, signal, effect, computed, input, untracked, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DashboardLayoutEditorComponent } from '../../../../shared/components/dashboard/dashboard-layout-editor.component';
import { CommonModule } from '@angular/common';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import {
  DashboardPageKey,
  CoolCreditGrowthSectionId,
  CoolCreditGrowthSectionDef,
} from '../../../../core/enums/dashboard-cards.enum';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilterByWidgetKeyMap, findCatalogPageId, resolveActiveCatalogProductId } from '../../../../shared/components/dashboard/dashboard-embed.helpers';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  buildLayoutMapFromEditorDrafts,
  computeLayoutEditorAutoWidthUpdates,
  getLiveSectionColClass,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { FormsModule } from '@angular/forms';
import { HorizontalBarChartComponent } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { FunnelChartComponent, IFunnelDataPoint } from '../../../../shared/components/charts/funnel-chart/funnel-chart.component';
import { DonutChartComponent, ChartData } from '../../../../shared/components/charts/donut-chart/donut-chart.component';
import { SingleBarComponent, BarChartData, ColorMap } from '../../../../shared/components/charts/bar-charts/single-bar/single-bar.component';
import { LineAreaChartComponent, ILineAreaSeries } from '../../../../shared/components/charts/line-area-chart/line-area-chart.component';
import { IconComponent, IconName } from '../../../../shared/icons/icon.component';
import { DatePickerComponent, DateRangePickerValue, DateRangePickerOutput } from '../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../core/services/api.service';
import dayjs from 'dayjs';
import { of, Observable } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { DataProgressBarComponent, ProgressBarData } from '../../../../shared/components/data-progress-bar/data-progress-bar.component';
import { ThemeService } from '../../../../core/services/theme.service';
import { RadarChartComponent, RadarData } from '../../../../shared/components/charts/radar-chart/radar-chart.component';
import { PieChartComponent } from '../../../../shared/components/charts/pie-chart/pie-chart.component';
import { DotChartComponent, DotChartData } from '../../../../shared/components/charts/dot-chart/dot-chart.component';
import { TooltipComponent } from "../../../../shared/components/ui/tooltip/tooltip.component";


// API Response Interfaces
interface CallSource {
  heading: string;
  sub_txt: string;
  value: {
    call_now_all: number;
    call_now_dash: string;
    call_now_pre_sim: string;
    call_now_post_sim: string;
    call_now_dispute: string;
    call_now_more: string;
    call_now_dash_int: number;
    call_now_pre_sim_int: number;
    call_now_post_sim_int: number;
    call_now_dispute_int: number;
    call_now_more_int: number;
  };
}

interface PlanLifecycleData extends BarChartData {
  label: string;
  count: number;
}

interface PaymentMethodStats {
  heading: string;
  sub_txt: string;
  value: {
    google_payment_count: number;
    apple_payment_count: number;
    card_payment_count: number;
    bank_payment_count: number;
  };
}

interface PurchasedStats {
  heading: string;
  sub_txt: string;
  value: {
    total_plan_purchased_count: number;
    starter_purchased_count: number;
    essential_purchased_count: number;
    premium_purchased_count: number;
    ultimate_purchased_count: number;
    payg_purchased_count: number;
  };
}

interface RevenueByPlan {
  heading: string;
  value: {
    revenue_generated: string;
    starter_amount: string;
    essential_amount: string;
    premium_amount: string;
    ultimate_amount: string;
    payg_amount: string;
  };
}

interface OnboardingStats {
  heading: string;
  value: {
    onboarding_step_1_pending: number;
    onboarding_step_2_pending: number;
    onboarding_step_3_pending: number;
    onboarding_no_credit_score: number;
    onboarding_success_credit_score: number;
  };
}

interface OnboardingFunnel {
  heading: string;
  value: {
    cbc_form_filled: number;
    credit_report_fetched: number;
    credit_report_not_fetched: number;
  };
}

interface ScoreRefreshStats {
  heading: string;
  value: {
    score_refresh_web: number;
    score_refresh_android: number;
    score_refresh_ios: number;
  };
}

interface PlanStats {
  heading: string;
  value: {
    plan_cancelled_count: number;
    plan_expired_count: number;
    plan_paused_count: number;
  };
}

interface InsightsStats {
  heading: string;
  value: string[];
}

interface DropOffStats {
  heading: string;
  value: {
    onboarding_dropoff: string;
    purchase_dropoff: string;
    call_dropoff: string;
  };
}

interface StatCard {
  title: string;
  value: number;
  icon: IconName;
  bgClass: string;
  iconColorClass: string;
  numberFormat?: string;
}

interface SectionLayoutItem {
  id: CoolCreditGrowthSectionId;
  visible: boolean;
  colSpan: number;
  order: number;
}

interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

interface DailyAnalytics {
  date: string;
  registered_users_count: number;
  through_sim_without_plan: number;
  through_sim_and_purchased: number;
  without_sim_with_plan: number;
  cbc_filled_all: number;
  cbc_report_fetched: number;
  cbc_report_not_fetched: number;
  health_report_users: number;
  disputes_created_count: number;
  scheduled_calls_all: number;
  credit_refresh_count_total: number;
  page_views_count: number;
  dispute_count: number;
  call_drop_off: number; // Added field
  call_source: CallSource;
  payment_method_stats: PaymentMethodStats;
  purchased_stats: PurchasedStats;
  revenue_by_plan: RevenueByPlan;
  onboarding_funnel: OnboardingFunnel;
  onboarding_stats: OnboardingStats;
  score_refresh_stats: ScoreRefreshStats;
  plan_stats: PlanStats;
  insights_stats: InsightsStats;
  drop_off_stats: DropOffStats; // Added field
}

interface PageTracking {
  page_id: number;
  page_name: string;
  activity_date: string;
  total_sessions: number;
  unique_users: number;
  total_time_spent_min: number;
}

interface DailySummaryResponse {
  data: DailyAnalytics[];
  page_tracking_arr: PageTracking[];
  insights_stats: InsightsStats; // Added field
  flag: number;
  is_error: number;
  msg: string;
  status: number;
}


@Component({
  selector: 'app-growth-activation-and-revenue',
  imports: [
    CommonModule,
    DashboardLayoutEditorComponent,
    FormsModule,
    HorizontalBarChartComponent,
    FunnelChartComponent,
    DonutChartComponent,
    SingleBarComponent,
    LineAreaChartComponent,
    IconComponent,
    DatePickerComponent,
    DataProgressBarComponent,
    RadarChartComponent,
    PieChartComponent,
    DotChartComponent,
    TooltipComponent,
  ],
  templateUrl: './growth-activation-and-revenue.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class GrowthActivationAndRevenueComponent implements OnInit {
  _themeService = inject(ThemeService);
  private apiService = inject(ApiService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);
  private cdr = inject(ChangeDetectorRef);

  // Cache and request sharing variables to prevent duplicate parallel API calls when multiple cards are rendered on custom dashboard
  private static cachedData: DailySummaryResponse | null = null;
  private static cachedPayloadKey: string | null = null;
  private static activeRequest$: Observable<DailySummaryResponse> | null = null;
  private static activeRequestKey: string | null = null;

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);
  readonly PAGE_KEY = DashboardPageKey.GROWTH_ACTIVATION_REVENUE;
  readonly SectionId = CoolCreditGrowthSectionId;
  readonly SECTIONS = CoolCreditGrowthSectionDef;

  isLoading = false;
  dailySummary: DailyAnalytics[] = [];
  pageTrackingData: PageTracking[] = [];
  globalInsights: InsightsStats | null = null;
  showAllInsights = false;

  // Metric Card Values (calculated from API data)
  totalRegisteredUsers = 0;
  totalRevenue = 0; // Alias for lifetimeRevenue
  totalDisputes = 0;
  creditRefreshCount = 0;
  totalPurchases = 0;
  cbcFormFilled = 0; // Alias for deficitEndedUsers
  // Legacy properties (can be removed if unused)
  totalSessions = 0;
  activeSessions = 0;
  deficitEndedUsers = 0;
  lifetimeRevenue = 0;

  // Dot Chart Metrics
  longestSession = 0;
  longestSessionPage = '';
  totalTimeSpend = 0;
  totalPages = 0;
  // Date Picker Configuration
  datePickerValue: DateRangePickerValue = {
    startDate: dayjs().subtract(90, 'day').startOf('day').toISOString(),
    endDate: dayjs().endOf('day').toISOString(),
    unit: 'day',
    comparisonType: 'none'
  };

  get shouldShowGroupBy(): boolean {
    const start = dayjs(this.datePickerValue.startDate);
    const end = dayjs(this.datePickerValue.endDate);
    return end.diff(start, 'days') + 1 >= 240;
  }

  get chartDateUnit(): 'day' | 'month' | 'quarter' | 'year' {
    let unit = this.datePickerValue.unit || 'month';
    // If unit is less than month (e.g. day), use month as per transformBarLineData logic
    if (unit === 'day' || unit === 'week') {
      return 'month';
    }
    return unit as 'day' | 'month' | 'quarter' | 'year';
  }

  constructor() {
    this._initLayout();
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      const productPid = this.apiService.selectedProductPid();
      if (!productId && productPid == null) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<CoolCreditGrowthSectionId>();
          keys.forEach((k) => {
            const mapped = this.WIDGET_KEY_TO_SECTION.get(k);
            if (mapped != null) {
              pinned.add(mapped);
              return;
            }
            if (Object.values(CoolCreditGrowthSectionId).includes(k as CoolCreditGrowthSectionId)) {
              pinned.add(k as CoolCreditGrowthSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<CoolCreditGrowthSectionId, number>();
            for (const sec of Object.values(CoolCreditGrowthSectionId)) {
              const mappedKey = this.SECTION_WIDGET_KEYS[sec];
              const id =
                (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });

    this._themeService.theme$.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.dailySummary.length > 0) {
        this.transformCallSources();
        this.transformPlanLifecycle();
      }
    });

    // Observe breakpoint for chart legend positioning
    this.breakpointObserver.observe('(max-width: 1536px)')
      .pipe(takeUntilDestroyed())
      .subscribe(result => {
        this.simulatorLegendPosition = result.matches ? 'bottom' : 'right';
      });
  }

  ngOnInit(): void {
    if (this.mixedMode()) {
      this.fetchDailySummary();
    }
  }

  // Date Range Change Handler
  onDateRangeChange(output: DateRangePickerOutput) {

    // Update the datePickerValue with the new dates
    this.datePickerValue = {
      startDate: output.start_date,
      endDate: output.end_date,
      unit: output.unit,
      comparisonType: 'none'
    };

    // Fetch data with the updated date range
    this.fetchDailySummary();
  }

  // Onboarding Funnel Data
  onboardingFunnelData: IFunnelDataPoint[] = [];

  // Revenue Contribution Data
  revenueContributionData: BarChartData[] = [];

  // Payment Method Distribution Data
  paymentMethodData: ChartData[] = [];
  paymentMethodTotal = 0;

  // Drop-off Comparison Data
  dropoffComparisonData: ProgressBarData[] = [];

  // Simulator Usage Distribution Data
  simulatorUsageData: ChartData[] = [];

  // CBC Effect Data
  cbcEffectData: ChartData[] = [];

  // Key Insights Data
  keyInsights: string[] = [];

  // Line-Area Chart Data (for Monthly Disputes & Calls)
  lineAreaSeries: ILineAreaSeries[] = [];

  // Responsive Legend Position
  simulatorLegendPosition: 'right' | 'bottom' = 'right';
  private breakpointObserver = inject(BreakpointObserver);

  isLayoutEditorTallTile = (item: { id: string }): boolean =>
    item.id === CoolCreditGrowthSectionId.CBC_EFFECT;


  callSourceData: BarChartData[] = [];
  callSourceRadarData: RadarData[] = [];
  summaryCards: { label: string, value: number, rightText: string, isBadge: boolean }[] = [];

  get statCards(): StatCard[] {
    return [
      {
        title: 'Total Registered Users',
        value: this.totalRegisteredUsers,
        icon: 'users',
        bgClass: 'bg-blue-100 dark:bg-blue-900/30',
        iconColorClass: 'text-blue-600 dark:text-blue-400'
      },
      {
        title: 'Total Revenue $',
        value: this.totalRevenue,
        icon: 'gift',
        bgClass: 'bg-purple-100 dark:bg-purple-900/30',
        iconColorClass: 'text-purple-600 dark:text-purple-400',
        numberFormat: '1.2-2'
      },
      {
        title: 'Total Disputes',
        value: this.totalDisputes,
        icon: 'fileText',
        bgClass: 'bg-orange-100 dark:bg-orange-900/30',
        iconColorClass: 'text-orange-600 dark:text-orange-400'
      },
      {
        title: 'Credit refresh count',
        value: this.creditRefreshCount,
        icon: 'refreshCcw',
        bgClass: 'bg-teal-100 dark:bg-teal-900/30',
        iconColorClass: 'text-teal-600 dark:text-teal-400'
      },
      {
        title: 'Plans Purchased',
        value: this.totalPurchases,
        icon: 'shoppingBag',
        bgClass: 'bg-indigo-100 dark:bg-indigo-900/30',
        iconColorClass: 'text-indigo-600 dark:text-indigo-400'
      },
      {
        title: 'CBC Form Filled',
        value: this.cbcFormFilled,
        icon: 'fileCheck',
        bgClass: 'bg-sky-100 dark:bg-sky-900/30',
        iconColorClass: 'text-sky-600 dark:text-sky-400'
      }
    ];
  }



  // Plan Lifecycle Data
  planLifecycleData: PlanLifecycleData[] = [];
  planLifecyclePieData: { label: string, value: number }[] = [];

  // Page Session Distribution Data
  pageSessionData: DotChartData[] = [];

  // ===== DATA PROCESSING METHODS =====

  parseMoneyValue(value: string): number {
    if (!value) return 0;
    return parseFloat(value.replace('$', '').replace(',', '')) || 0;
  }

  processApiData(data: DailyAnalytics[], pageTracking: PageTracking[], insightsStats?: InsightsStats) {
    this.dailySummary = data;
    this.pageTrackingData = pageTracking;
    this.globalInsights = insightsStats || null;

    // Calculate metrics
    this.calculateMetrics();

    // Transform all chart data
    this.transformOnboardingFunnel();
    this.transformRevenueContribution();
    this.transformPaymentMethods();
    this.transformSimulatorUsage();
    this.transformCallSources();
    this.transformPlanLifecycle();
    this.transformBarLineData();
    this.transformPageSessions();
    this.transformCbcEffect();
    this.transformDropoffComparison();
    this.extractKeyInsights();
  }

  calculateMetrics() {
    // Sum all registered users
    this.totalRegisteredUsers = this.dailySummary.reduce((sum, day) => sum + day.registered_users_count, 0);

    // Calculate total sessions from page tracking
    const uniqueDates = new Set(this.pageTrackingData.map(p => p.activity_date));
    this.totalSessions = this.pageTrackingData.reduce((sum, page) => sum + page.total_sessions, 0);

    // Active sessions (unique users from page tracking)
    this.activeSessions = this.pageTrackingData.reduce((sum, page) => sum + page.unique_users, 0);

    // Calculate CBC filled users (deficit-ended users)
    this.deficitEndedUsers = this.dailySummary.reduce((sum, day) => sum + day.cbc_filled_all, 0);
    this.cbcFormFilled = this.deficitEndedUsers; // Alias for card

    // Total disputes
    this.totalDisputes = this.dailySummary.reduce((sum, day) => sum + day.dispute_count, 0);

    // Credit refresh count
    this.creditRefreshCount = this.dailySummary.reduce((sum, day) => sum + day.credit_refresh_count_total, 0);

    // Total purchases (sum of all plan purchases)
    // Using purchased_stats.value.total_plan_purchased_count
    this.totalPurchases = this.dailySummary.reduce((sum, day) =>
      sum + (day.purchased_stats?.value?.total_plan_purchased_count || 0), 0);

    // Lifetime revenue
    // Using revenue_by_plan.value.revenue_generated
    this.lifetimeRevenue = this.dailySummary.reduce((sum, day) =>
      sum + this.parseMoneyValue(day.revenue_by_plan?.value?.revenue_generated), 0);
    this.totalRevenue = Math.round((this.lifetimeRevenue * 100)) / 100; // Alias for card
  }

  transformOnboardingFunnel() {
    // Aggregate onboarding funnel data
    // const step1 = this.dailySummary.reduce((sum, day) => sum + day.registered_users_count, 0);
    const step2 = this.dailySummary.reduce((sum, day) => sum + (day.onboarding_stats?.value?.onboarding_step_2_pending || 0), 0);
    const step3 = this.dailySummary.reduce((sum, day) => sum + (day.onboarding_stats?.value?.onboarding_step_3_pending || 0), 0);
    const noCreditScore = this.dailySummary.reduce((sum, day) => sum + (day.onboarding_stats?.value?.onboarding_no_credit_score || 0), 0);
    const creditScore = this.dailySummary.reduce((sum, day) => sum + (day.onboarding_stats?.value?.onboarding_success_credit_score || 0), 0);

    const total = this.totalRegisteredUsers || 1;

    this.onboardingFunnelData = [
      {
        label: Math.round((step2 / total) * 100).toString() + '%',
        value: step2,
        percentage: Math.round((step2 / total) * 100),
        color: '#3b82f6',
        leftLabel: 'Step 1 done',
        rightLabel: 'Step 2 pending',
      },
      {
        label: Math.round((step3 / total) * 100).toString() + '%',
        value: step3,
        percentage: Math.round((step3 / total) * 100),
        color: '#6366f1',
        leftLabel: 'Step 2 done',
        rightLabel: 'Step 3 pending',
      },
      {
        label: Math.round((noCreditScore / total) * 100).toString() + '%',
        value: noCreditScore,
        percentage: Math.round((noCreditScore / total) * 100),
        color: '#06b6d4',
        leftLabel: 'Step 3 done',
        rightLabel: 'Credit Not Fetched',
      },
      {
        label: Math.round((creditScore / total) * 100).toString() + '%',
        value: creditScore,
        percentage: Math.round((creditScore / total) * 100),
        color: '#10b981',
        leftLabel: 'Step 3 done',
        rightLabel: 'Credit Fetched',
      },
    ];
    this.onboardingFunnelData.sort((a, b) => b.percentage - a.percentage);
  }

  transformRevenueContribution() {
    // Aggregate revenue by plan type
    const starterRevenue = this.dailySummary.reduce((sum, day) => sum + this.parseMoneyValue(day.revenue_by_plan?.value?.starter_amount), 0);
    const essentialRevenue = this.dailySummary.reduce((sum, day) => sum + this.parseMoneyValue(day.revenue_by_plan?.value?.essential_amount), 0);
    const premiumRevenue = this.dailySummary.reduce((sum, day) => sum + this.parseMoneyValue(day.revenue_by_plan?.value?.premium_amount), 0);
    const ultimateRevenue = this.dailySummary.reduce((sum, day) => sum + this.parseMoneyValue(day.revenue_by_plan?.value?.ultimate_amount), 0);
    const paygRevenue = this.dailySummary.reduce((sum, day) => sum + this.parseMoneyValue(day.revenue_by_plan?.value?.payg_amount), 0);

    const startDate = this.dailySummary.length > 0 ? this.dailySummary[0].date : '';
    const endDate = this.dailySummary.length > 0 ? this.dailySummary[this.dailySummary.length - 1].date : '';

    this.revenueContributionData = [
      { start: startDate, end: endDate, label: 'Starter Plan', revenue: starterRevenue, color: '#6DA5FF' },
      { start: startDate, end: endDate, label: 'Essential Plan', revenue: essentialRevenue, color: '#A983FF' },
      { start: startDate, end: endDate, label: 'Premium Plan', revenue: premiumRevenue, color: '#2ADFA3' },
      { start: startDate, end: endDate, label: 'Ultimate Plan', revenue: ultimateRevenue, color: '#FFB841' },
      { start: startDate, end: endDate, label: 'Pay as you go', revenue: paygRevenue, color: '#FF6BB4' },
    ];
  }

  transformPaymentMethods() {
    const google = this.dailySummary.reduce((sum, day) => sum + (day.payment_method_stats?.value?.google_payment_count || 0), 0);
    const apple = this.dailySummary.reduce((sum, day) => sum + (day.payment_method_stats?.value?.apple_payment_count || 0), 0);
    const card = this.dailySummary.reduce((sum, day) => sum + (day.payment_method_stats?.value?.card_payment_count || 0), 0);
    const bank = this.dailySummary.reduce((sum, day) => sum + (day.payment_method_stats?.value?.bank_payment_count || 0), 0);

    this.paymentMethodData = [
      { label: 'Google Pay', value: google, color: '#8B5CF6' },
      { label: 'Apple Pay', value: apple, color: '#FFA514' },
      { label: 'Card Payment', value: card, color: '#0C66E8' },
      { label: 'Bank Transfer', value: bank, color: '#15B268' },
    ].filter(item => item.value > 0);

    // Sort by value descending so largest rings are outside (if concentric)
    this.paymentMethodData.sort((a, b) => b.value - a.value);
    this.paymentMethodTotal = this.paymentMethodData.reduce((sum, item) => sum + item.value, 0);
  }

  transformSimulatorUsage() {
    const simNoPurchase = this.dailySummary.reduce((sum, day) => sum + (day.through_sim_without_plan || 0), 0);
    const simBlockedBooster = this.dailySummary.reduce((sum, day) => sum + (day.without_sim_with_plan || 0), 0);
    const simBooster = this.dailySummary.reduce((sum, day) => sum + (day.through_sim_and_purchased || 0), 0);

    this.simulatorUsageData = [
      { label: 'Simulator → No Purchase', value: simNoPurchase, color: '#15B268' },
      { label: 'Simulator Blocked → Booster Purchased', value: simBlockedBooster, color: '#0C66E8' },
      { label: 'Simulator → Booster Purchased', value: simBooster, color: '#8B5CF6' },
    ].filter(item => item.value > 0);
  }

  transformCallSources() {
    const dash = this.dailySummary.reduce((sum, day) => sum + (day.call_source?.value?.call_now_dash_int || 0), 0);
    const preSim = this.dailySummary.reduce((sum, day) => sum + (day.call_source?.value?.call_now_pre_sim_int || 0), 0);
    const postSim = this.dailySummary.reduce((sum, day) => sum + (day.call_source?.value?.call_now_post_sim_int || 0), 0);
    const dispute = this.dailySummary.reduce((sum, day) => sum + (day.call_source?.value?.call_now_dispute_int || 0), 0);
    const more = this.dailySummary.reduce((sum, day) => sum + (day.call_source?.value?.call_now_more_int || 0), 0);

    const startDate = this.dailySummary.length > 0 ? this.dailySummary[0].date : '';
    const endDate = this.dailySummary.length > 0 ? this.dailySummary[this.dailySummary.length - 1].date : '';

    const color = this._themeService.theme() === 'dark' ? '#A1A1AA' : '#7D7D93';


    this.callSourceData = [
      { start: startDate, end: endDate, label: 'Dashboard', count: dash, color: '#0C66E8' },
      { start: startDate, end: endDate, label: 'Before Simulator', count: preSim, color: '#FFA514' },
      { start: startDate, end: endDate, label: 'After Simulator', count: postSim, color: '#AD46FF' },
      { start: startDate, end: endDate, label: 'Dispute Page', count: dispute, color: '#F05050' },
      { start: startDate, end: endDate, label: 'More Section', count: more, color: color },
    ];

    // Prepare data for Radar Chart
    this.callSourceRadarData = [
      { label: 'Dashboard', value: dash },
      { label: 'Before Simulator', value: preSim },
      { label: 'After Simulator', value: postSim },
      { label: 'Dispute Page', value: dispute },
      { label: 'More Section', value: more },
    ];

    // Calculate totals and top source
    const totalCallSourceCount = this.callSourceRadarData.reduce((sum, item) => sum + item.value, 0);
    const topCallSource = this.callSourceRadarData.reduce((prev, current) => (prev.value > current.value) ? prev : current, { label: '', value: 0 });

    this.summaryCards = [
      {
        label: topCallSource.label,
        value: topCallSource.value,
        rightText: 'Top Source',
        isBadge: true
      },
      {
        label: 'Total Calls',
        value: totalCallSourceCount,
        rightText: 'Across all source',
        isBadge: false
      }
    ];
  }

  transformPlanLifecycle() {
    const purchased = this.totalPurchases;
    const cancelled = this.dailySummary.reduce((sum, day) => sum + (day.plan_stats?.value?.plan_cancelled_count || 0), 0);
    const expired = this.dailySummary.reduce((sum, day) => sum + (day.plan_stats?.value?.plan_expired_count || 0), 0);
    const paused = this.dailySummary.reduce((sum, day) => sum + (day.plan_stats?.value?.plan_paused_count || 0), 0);

    const startDate = this.dailySummary.length > 0 ? this.dailySummary[0].date : '';
    const endDate = this.dailySummary.length > 0 ? this.dailySummary[this.dailySummary.length - 1].date : '';

    this.planLifecycleData = [
      { start: startDate, end: endDate, label: 'Purchased', count: purchased, color: '#10B981' },
      { start: startDate, end: endDate, label: 'Cancelled', count: cancelled, color: '#F5405A' },
      { start: startDate, end: endDate, label: 'Expired', count: expired, color: '#7D7D93' },
      { start: startDate, end: endDate, label: 'Paused', count: paused, color: '#FFA514' },
    ];

    this.planLifecyclePieData = this.planLifecycleData.map(d => ({
      label: d.label,
      value: d.count
    }));
  }

  transformBarLineData() {
    // Transform daily data into aggregated format based on selected unit
    const monthlyData = new Map<string, any>();
    let unit = this.datePickerValue.unit || 'month';

    // If unit is less than month (e.g. day), use month
    if (unit === 'day' || unit === 'week') {
      unit = 'month';
    }

    this.dailySummary.forEach(day => {
      let periodKey = '';
      let label = '';
      let dateStr = ''; // Using YYYY-MM-DD format

      const d = dayjs(day.date);

      if (unit === 'quarter') {
        const q = Math.ceil((d.month() + 1) / 3);
        periodKey = `${d.year()}-Q${q}`;
        label = `Q${q} ${d.year()}`;
        // Use first day of the quarter
        const startMonth = (q - 1) * 3;
        dateStr = d.month(startMonth).startOf('month').format('YYYY-MM-DD');
      } else if (unit === 'year') {
        periodKey = d.format('YYYY');
        label = periodKey;
        dateStr = d.startOf('year').format('YYYY-MM-DD');
      } else {
        // Month (default fallback)
        periodKey = d.format('YYYY-MM');
        label = d.format('MMM YYYY');
        dateStr = d.startOf('month').format('YYYY-MM-DD');
      }

      if (!monthlyData.has(periodKey)) {
        monthlyData.set(periodKey, {
          date: dateStr,
          label: label,
          total_disputes: 0,
          total_calls: 0,
          scheduled_calls: 0
        });
      }

      const current = monthlyData.get(periodKey);
      current.total_disputes += day.dispute_count;
      // Aggregating call_now_all + scheduled_calls_all for total calls
      current.total_calls += ((day.call_source?.value?.call_now_all || 0) + day.scheduled_calls_all);
      current.scheduled_calls += day.scheduled_calls_all;
    });

    // Convert Map to array and sort by date
    const sortedData = Array.from(monthlyData.values())
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

    // Create series data for line-area chart
    this.lineAreaSeries = [
      {
        key: 'total_disputes',
        name: 'Total Disputes',
        data: sortedData.map(d => ({ date: d.date, value: d.total_disputes, label: d.label })),
        lineColor: '#8B5CF6', // Orange for disputes
        areaColor: '#8B5CF6',
        areaOpacity: 0.85,
        renderAsBar: false, // Render as vertical bars
        barWidth: 0.5, // Bar width
        strokeWidth: 0,
        showValues: false // Show value labels for this series
      },
      {
        key: 'total_calls',
        name: 'Total Calls',
        data: sortedData.map(d => ({ date: d.date, value: d.total_calls, label: d.label })),
        lineColor: '#0C66E8', // Blue for total calls
        areaColor: '#0C66E8', // Show area fill
        areaOpacity: 0.3,
        strokeWidth: 2,
        renderAsBar: false, // Explicitly set to false for line series
        showPoints: true,
        showValues: true,
        pointRadius: 4
      },
      {
        key: 'scheduled_calls',
        name: 'Scheduled Calls',
        data: sortedData.map(d => ({ date: d.date, value: d.scheduled_calls, label: d.label })),
        lineColor: '#FFA514', // Green for scheduled calls
        // No areaColor - line only
        strokeWidth: 2,
        renderAsBar: false, // Explicitly set to false for line series
        showPoints: true,
        pointRadius: 3
      }
    ];
  }


  transformPageSessions() {
    // Group page tracking by page name and sum sessions
    const pageMap = new Map<string, number>();

    this.pageTrackingData.forEach(page => {
      const current = pageMap.get(page.page_name) || 0;
      pageMap.set(page.page_name, current + page.total_sessions);
    });

    const colors = [
      '#3b82f6', // Blue
      '#8B5CF6', // Purple
      '#10B981', // Green
      '#F59E0B', // Orange
      '#EC4899', // Pink
      '#EF4444', // Red
      '#06B6D4', // Cyan
      '#84CC16', // Lime
      '#6366F1', // Indigo
      '#14B8A6'  // Teal
    ];

    // Convert to array and sort by value
    this.pageSessionData = Array.from(pageMap.entries())
      .map(([label, value], index) => ({
        label,
        value,
        color: colors[index % colors.length]
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10 pages

    // Calculate metrics for Dot Chart section
    if (this.pageSessionData.length > 0) {
      const topPage = this.pageSessionData[0]; // Already sorted desc
      this.longestSession = topPage.value;
      this.longestSessionPage = topPage.label;

      this.totalTimeSpend = this.pageSessionData.reduce((sum, item) => sum + item.value, 0);
      this.totalPages = this.pageSessionData.length;
    } else {
      this.longestSession = 0;
      this.longestSessionPage = '';
      this.totalTimeSpend = 0;
      this.totalPages = 0;
    }
  }

  transformCbcEffect() {
    const cbcFilled = this.dailySummary.reduce((sum, day) => sum + (day.onboarding_funnel?.value?.cbc_form_filled || 0), 0);
    const creditFetched = this.dailySummary.reduce((sum, day) => sum + (day.onboarding_funnel?.value?.credit_report_fetched || 0), 0);
    const creditNotFetched = this.dailySummary.reduce((sum, day) => sum + (day.onboarding_funnel?.value?.credit_report_not_fetched || 0), 0);

    const startDate = this.dailySummary.length > 0 ? this.dailySummary[0].date : '';
    const endDate = this.dailySummary.length > 0 ? this.dailySummary[this.dailySummary.length - 1].date : '';

    this.cbcEffectData = [
      { start: startDate, end: endDate, label: 'CBC Forms Filled', value: cbcFilled, color: '#6DA5FF', gradientStart: '#6DA5FF', gradientEnd: '#2563EB' },
      { start: startDate, end: endDate, label: 'Credit Report Fetched', value: creditFetched, color: '#2ADFA3', gradientStart: '#2ADFA3', gradientEnd: '#10B981' },
      { start: startDate, end: endDate, label: 'Credit Report Not Fetched', value: creditNotFetched, color: '#FFB841', gradientStart: '#FFB841', gradientEnd: '#F59E0B' }
    ].filter(item => item.value > 0);
  }

  transformDropoffComparison() {
    let onboardingDropoff = 0;
    let purchaseDropoff = 0;
    let callDropoff = 0;

    if (this.dailySummary.length > 0) {
      const parsePercentage = (value: string | undefined) => {
        if (!value) return 0;
        return parseFloat(value.replace('%', ''));
      };

      let totalOnboarding = 0;
      let totalPurchase = 0;
      let totalCall = 0;
      let count = 0;

      this.dailySummary.forEach(day => {
        if (day.drop_off_stats?.value) {
          totalOnboarding += parsePercentage(day.drop_off_stats.value.onboarding_dropoff);
          totalPurchase += parsePercentage(day.drop_off_stats.value.purchase_dropoff);
          totalCall += parsePercentage(day.drop_off_stats.value.call_dropoff);
          count++;
        }
      });

      if (count > 0) {
        onboardingDropoff = Math.max(0, totalOnboarding / count);
        purchaseDropoff = Math.max(0, totalPurchase / count);
        callDropoff = Math.max(0, totalCall / count);
      }
    }

    this.dropoffComparisonData = [
      { label: 'Onboarding Drop-off', value: parseFloat(onboardingDropoff.toFixed(1)), color: '#F05050' }, // Red for high dropoff
      { label: 'Purchase Drop-off', value: parseFloat(purchaseDropoff.toFixed(1)), color: '#FFA514' }, // Orange
      { label: 'Call Drop-off', value: parseFloat(callDropoff.toFixed(1)), color: '#AD46FF' } // Purple
    ];
  }

  extractKeyInsights() {
    // Use insights from the API response (globalInsights) if available,
    // otherwise fallback to the most recent day in the summary
    if (this.globalInsights && this.globalInsights.value?.length > 0) {
      this.keyInsights = this.globalInsights.value;
    } else if (this.dailySummary.length > 0) {
      // Sort by date descending to get the latest
      const latestDay = [...this.dailySummary].sort((a, b) => dayjs(b.date).diff(dayjs(a.date)))[0];
      this.keyInsights = latestDay.insights_stats?.value || [];
    } else {
      this.keyInsights = [];
    }
  }

  fetchDailySummary() {
    this.isLoading = true;
    this.cdr.markForCheck(); // Ensure the loader spinner displays immediately

    // Convert ISO dates to MM/DD/YYYY format using UTC to prevent timezone shifts
    const formatDate = (date: string): string => {
      return dayjs.utc(date).format('MM/DD/YYYY');
    };

    const payload = {
      start_date: formatDate(this.datePickerValue.startDate),
      end_date: formatDate(this.datePickerValue.endDate),
    };

    const payloadKey = `${payload.start_date}_${payload.end_date}`;

    // 1. If we have a cached response for this exact payload, use it immediately
    if (GrowthActivationAndRevenueComponent.cachedPayloadKey === payloadKey && GrowthActivationAndRevenueComponent.cachedData) {
      const response = JSON.parse(JSON.stringify(GrowthActivationAndRevenueComponent.cachedData));
      setTimeout(() => {
        this.isLoading = false;
        if (response && response.data) {
          this.processApiData(response.data, response.page_tracking_arr || [], response.insights_stats);
        }
        this.cdr.markForCheck(); // Trigger change detection to bind data and hide loader
      }, 0);
      return;
    }

    const headers = {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
      'Access-Control-Allow-Headers': 'Content-Type, Authorization',
    };

    // 2. If there is already an in-flight request for this exact payload, share it
    if (GrowthActivationAndRevenueComponent.activeRequestKey === payloadKey && GrowthActivationAndRevenueComponent.activeRequest$) {
      GrowthActivationAndRevenueComponent.activeRequest$.subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response && response.data) {
            const clonedResponse = JSON.parse(JSON.stringify(response));
            this.processApiData(clonedResponse.data, clonedResponse.page_tracking_arr || [], clonedResponse.insights_stats);
          }
          this.cdr.markForCheck(); // Trigger change detection for all shared subscribers
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Shared Daily Summary API Error:', error);
          this.cdr.markForCheck();
        }
      });
      return;
    }

    // 3. Otherwise, initiate a new request and cache/share it
    const request$ = this.apiService
      .post<DailySummaryResponse>('ai/v1/users/daily_summary', payload, {
        addAccessToken: false,
        productServerName: 'cool-credit',
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
        headers: headers
      })
      .pipe(
        shareReplay(1)
      );

    GrowthActivationAndRevenueComponent.activeRequestKey = payloadKey;
    GrowthActivationAndRevenueComponent.activeRequest$ = request$;

    request$.subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response && response.data) {
          // Store in cache
          GrowthActivationAndRevenueComponent.cachedPayloadKey = payloadKey;
          GrowthActivationAndRevenueComponent.cachedData = response;
          // Process data
          const clonedResponse = JSON.parse(JSON.stringify(response));
          this.processApiData(clonedResponse.data, clonedResponse.page_tracking_arr || [], clonedResponse.insights_stats);
        }
        this.cdr.markForCheck(); // Trigger change detection
        // Cleanup in-flight request tracker once completed
        if (GrowthActivationAndRevenueComponent.activeRequestKey === payloadKey) {
          GrowthActivationAndRevenueComponent.activeRequest$ = null;
          GrowthActivationAndRevenueComponent.activeRequestKey = null;
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error('Daily Summary API Error:', error);
        this.cdr.markForCheck();
        // Cleanup in-flight request tracker on error
        if (GrowthActivationAndRevenueComponent.activeRequestKey === payloadKey) {
          GrowthActivationAndRevenueComponent.activeRequest$ = null;
          GrowthActivationAndRevenueComponent.activeRequestKey = null;
        }
      },
    });
  }

  showInsightsModal = false;

  readonly layoutMap = signal<Record<CoolCreditGrowthSectionId, SectionLayoutItem>>(
    {} as Record<CoolCreditGrowthSectionId, SectionLayoutItem>,
  );

  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  showSection(sectionId: CoolCreditGrowthSectionId): boolean {
    if (this.mixedMode()) {
      return this.embeddedVisibleSections().some((s) => s.id === sectionId);
    }
    const map = this.layoutMap();
    if (!Object.keys(map).length) return true;
    if (!map[sectionId]) return true;
    return map[sectionId].visible !== false;
  }

  sectionOrder(sectionId: CoolCreditGrowthSectionId): number {
    return this.layoutMap()[sectionId]?.order ?? 0;
  }

  getSectionColClass(sectionId: CoolCreditGrowthSectionId): string {
    const span =
      this.layoutMap()[sectionId]?.colSpan ??
      this.DEFAULT_COL_SPAN[sectionId] ??
      6;
    return getLiveSectionColClass(span);
  }

  /**
   * Stretch chart / insight tiles in My Dashboard mixed embed.
   * Stat cards are a single compact row — never force min-height on them.
   */
  sectionShellHeightClass(sectionId: CoolCreditGrowthSectionId): string {
    if (!this.mixedMode()) return '';
    if (sectionId === CoolCreditGrowthSectionId.STAT_CARDS) return '';
    return 'h-full min-h-[300px]';
  }

  rowDisplayOrder(sectionIds: CoolCreditGrowthSectionId[]): number {
    const orders = sectionIds
      .filter((id) => this.showSection(id))
      .map((id) => this.sectionOrder(id));
    return orders.length ? Math.min(...orders) : 9999;
  }

  chartPairGridClass(sectionIds: CoolCreditGrowthSectionId[]): string {
    const visibleCount = sectionIds.filter((id) => this.showSection(id)).length;
    return visibleCount <= 1
      ? 'grid grid-cols-1 gap-4'
      : 'grid grid-cols-1 lg:grid-cols-2 gap-4';
  }

  cbcPaymentRowGridClass(): string {
    const cbc = this.showSection(CoolCreditGrowthSectionId.CBC_EFFECT);
    const side =
      this.showSection(CoolCreditGrowthSectionId.PAYMENT_METHOD) ||
      this.showSection(CoolCreditGrowthSectionId.SIMULATOR_USAGE);
    if (cbc && side) return 'grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 items-stretch';
    return 'grid grid-cols-1 gap-4';
  }

  readonly pinnedSections = signal<Set<CoolCreditGrowthSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<CoolCreditGrowthSectionId>>(new Set());

  readonly pinnableWidgetIds = signal<Map<CoolCreditGrowthSectionId, number>>(
    new Map(),
  );

  /**
   * Frontend section id (`cc_gar_*`) → backend catalog `widget_key`
   * (`co_ga_*`) mapping. Backend catalog under product Cool Credit
   * (`pages.id = 40`) registers widgets with `co_ga_` prefixes that
   * differ from the frontend enum values.
   */
  private readonly SECTION_WIDGET_KEYS: Partial<Record<CoolCreditGrowthSectionId, string>> = {
    [CoolCreditGrowthSectionId.STAT_CARDS]: 'co_ga_growth_revenue_counters',
    [CoolCreditGrowthSectionId.KEY_INSIGHTS]: 'co_ga_growth_key_insights',
    [CoolCreditGrowthSectionId.DROPOFF_COMPARISON]: 'co_ga_journey_dropoff_comparison',
    [CoolCreditGrowthSectionId.ONBOARDING_FUNNEL]: 'co_ga_onboarding_funnel',
    [CoolCreditGrowthSectionId.REVENUE_CONTRIBUTION]: 'co_ga_revenue_by_plan',
    [CoolCreditGrowthSectionId.CBC_EFFECT]: 'co_ga_cbc_fetch_outcomes',
    [CoolCreditGrowthSectionId.PAYMENT_METHOD]: 'co_ga_payment_method_distribution',
    [CoolCreditGrowthSectionId.SIMULATOR_USAGE]: 'co_ga_simulator_usage_distribution',
    [CoolCreditGrowthSectionId.MONTHLY_DISPUTES_CALLS]: 'co_ga_disputes_calls_trend',
    [CoolCreditGrowthSectionId.CALL_SOURCE]: 'co_ga_call_source_distribution',
    [CoolCreditGrowthSectionId.PLAN_LIFECYCLE]: 'co_ga_plan_lifecycle_status',
    [CoolCreditGrowthSectionId.PAGE_SESSION]: 'co_ga_page_session_distribution',
  };

  private readonly WIDGET_KEY_TO_SECTION: ReadonlyMap<string, CoolCreditGrowthSectionId> =
    new Map(
      (Object.entries(this.SECTION_WIDGET_KEYS) as [
        CoolCreditGrowthSectionId,
        string,
      ][]).map(([sec, key]) => [key, sec]),
    );

  readonly embeddedVisibleSections = createEmbeddedSectionFilterByWidgetKeyMap(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.WIDGET_KEY_TO_SECTION,
    this.orderedSections,
  );

  isAddedToCustomDashboard(sectionId: CoolCreditGrowthSectionId): boolean {
    return this.pinnedSections().has(sectionId);
  }

  isAddingToCustomDashboard(sectionId: CoolCreditGrowthSectionId): boolean {
    return this.pendingPinSections().has(sectionId);
  }

  togglePinToCustomDashboard(sectionId: CoolCreditGrowthSectionId): void {
    if (this.pendingPinSections().has(sectionId)) return;

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);
    const productId = resolveActiveCatalogProductId(this.apiService);
    if (productId == null) return;

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const fire = (resolvedPageId: number) => {
      const widgetId = this.pinnableWidgetIds().get(sectionId);
      if (widgetId != null) {
        this.customDashboardPin.toggleLayoutItem({
          pageId: resolvedPageId,
          widgetId,
          sourcePageId: resolvedPageId,
          colSpan,
          rowSpan: 1,
          customConfig: {},
          productId,
          isCurrentlyPinned,
          onPinnedChange,
          onPendingChange,
        });
        return;
      }

      this.customDashboardPin.toggleSection({
        pageId: resolvedPageId,
        widgetKey: this.SECTION_WIDGET_KEYS[sectionId] ?? sectionId,
        colSpan,
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
    };

    const cachedPageId = this.effectiveCatalogPageId();
    if (cachedPageId != null) {
      fire(cachedPageId);
      return;
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId = findCatalogPageId(
          catalog as any[],
          this.PAGE_KEY,
          productId,
        );
        if (resolvedPageId == null) return;

        this.effectiveCatalogPageId.set(resolvedPageId);

        if (this.pinnableWidgetIds().get(sectionId) == null) {
          this.customDashboardPin
            .resolveCatalogWidgetIdsForPage(resolvedPageId, productId)
            .subscribe((widgetIdMap) => {
              const next = new Map(this.pinnableWidgetIds());
              for (const id of Object.values(CoolCreditGrowthSectionId)) {
                const mappedKey = this.SECTION_WIDGET_KEYS[id];
                const wid =
                  (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                  widgetIdMap.get(id);
                if (wid != null) next.set(id, wid);
              }
              this.pinnableWidgetIds.set(next);
              fire(resolvedPageId);
            });
          return;
        }

        fire(resolvedPageId);
      });
  }

  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );
  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  private _layoutSnapshot: Record<CoolCreditGrowthSectionId, SectionLayoutItem> | null =
    null;

  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];
  private readonly LAYOUT_LS_KEY = 'prism_cc_growth_activation_layout_v1';

  private readonly DEFAULT_COL_SPAN: Record<CoolCreditGrowthSectionId, number> = {
    [CoolCreditGrowthSectionId.STAT_CARDS]: 12,
    [CoolCreditGrowthSectionId.KEY_INSIGHTS]: 6,
    [CoolCreditGrowthSectionId.DROPOFF_COMPARISON]: 6,
    [CoolCreditGrowthSectionId.ONBOARDING_FUNNEL]: 6,
    [CoolCreditGrowthSectionId.REVENUE_CONTRIBUTION]: 6,
    [CoolCreditGrowthSectionId.CBC_EFFECT]: 8,
    [CoolCreditGrowthSectionId.PAYMENT_METHOD]: 4,
    [CoolCreditGrowthSectionId.SIMULATOR_USAGE]: 4,
    [CoolCreditGrowthSectionId.MONTHLY_DISPUTES_CALLS]: 12,
    [CoolCreditGrowthSectionId.CALL_SOURCE]: 6,
    [CoolCreditGrowthSectionId.PLAN_LIFECYCLE]: 6,
    [CoolCreditGrowthSectionId.PAGE_SESSION]: 12,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    this.editorItems.set(
      this.orderedSections().map((s) => ({
        id: s.id,
        title: titleById.get(s.id) ?? s.id,
        visible: s.visible,
        colSpan: this._normalizeColSpan(s.colSpan),
        order: s.order,
        defaultColSpan: this._normalizeColSpan(
          this.DEFAULT_COL_SPAN[s.id as CoolCreditGrowthSectionId] ?? 6,
        ),
      })),
    );
    this._layoutSnapshot = JSON.parse(JSON.stringify(this.layoutMap()));
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    if (this._layoutSnapshot) {
      this.layoutMap.set(this._layoutSnapshot);
      this._layoutSnapshot = null;
    }
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: CoolCreditGrowthSectionId): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
    this._syncLayoutFromEditor();
  }

  resetLayoutEditorToDefaults(): void {
    this.editorItems.set(
      this.SECTIONS.map((s, idx) => {
        const defaultSpan = this._normalizeColSpan(
          this.DEFAULT_COL_SPAN[s.id as CoolCreditGrowthSectionId] ?? 6,
        );
        return {
          id: s.id as CoolCreditGrowthSectionId,
          title: s.title,
          visible: true,
          colSpan: defaultSpan,
          order: idx,
          defaultColSpan: defaultSpan,
        };
      }),
    );
    this._syncLayoutFromEditor();
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout = this._buildLayoutFromEditor();
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);
    this._layoutSnapshot = null;

    this._persistLayoutToBackend(draft);

    this.isLayoutEditorOpen.set(false);
  }

  /**
   * Writes the editor draft to the user's per-page personalized
   * dashboard via {@link CustomizeLayoutBatchService} — same flow as
   * Cool Credit Dashboard / Practina.
   */
  private _persistLayoutToBackend(
    draft: ReadonlyArray<LayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    const toBackendKeysDraft = (resolvedPageId: number) =>
      draft.map((d, idx) => ({
        key: this.SECTION_WIDGET_KEYS[d.id] ?? String(d.id),
        sourcePageId: resolvedPageId,
        colSpan: this._normalizeColSpan(d.colSpan),
        visible: d.visible,
        customConfig: null,
        order: idx,
      }));

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d, idx) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: idx,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId =
          cachedPageId ??
          findCatalogPageId(catalog as any[], this.PAGE_KEY, productId);

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        if (cachedPageId == null) {
          this.effectiveCatalogPageId.set(resolvedPageId);
        }

        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: resolvedPageId,
          productId,
          fallbackSourcePageId: resolvedPageId,
          draft: toBackendKeysDraft(resolvedPageId),
        });
      });
  }

  private _warnCatalogMiss(
    catalog: any[],
    productId: string | number | null,
  ): void {
    const scopedProducts =
      productId != null && String(productId).trim() !== ''
        ? (catalog ?? []).filter(
          (p: any) => String(p?.id) === String(productId),
        )
        : (catalog ?? []);

    const productsForLog =
      scopedProducts.length > 0 ? scopedProducts : (catalog ?? []);

    const pageNames: string[] = [];
    for (const product of productsForLog) {
      for (const page of product?.pages ?? []) {
        const name = String(page?.name ?? '').trim();
        if (name) pageNames.push(name);
      }
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[GrowthActivationAndRevenue] No catalog page found for ` +
      `"${this.PAGE_KEY}" (active product=${productId}). ` +
      `Catalog advertised pages for this product: ` +
      `${pageNames.length ? pageNames.join(', ') : '(none)'}. ` +
      `If the correct page is in the list above, add a matching ` +
      `substring to CATALOG_PAGE_HINTS[GROWTH_ACTIVATION_REVENUE] ` +
      `in dashboard-embed.helpers.ts.`,
    );
  }

  decreaseEditorCardColSpan(id: CoolCreditGrowthSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        const minSpan = this._normalizeColSpan(this.DEFAULT_COL_SPAN[i.id] ?? 6);
        if (nextSpan < minSpan) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
    this._syncLayoutFromEditor();
  }

  increaseEditorCardColSpan(id: CoolCreditGrowthSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
    this._syncLayoutFromEditor();
  }

  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width).
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(
      this.editorItems(),
      (i) => this.isLayoutEditorTallTile(i as LayoutEditorItem),
    );
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
    this._syncLayoutFromEditor();
  }

  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    const minSpan = this._normalizeColSpan(this.DEFAULT_COL_SPAN[item.id] ?? 6);
    return this.COL_SPAN_STEPS[idx - 1] >= minSpan;
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'col-span-4';
      case 6: return 'col-span-6';
      case 8: return 'col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'Small';
      case 6: return 'Medium';
      case 8: return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }

  /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  }
  private _buildLayoutFromEditor(): Record<CoolCreditGrowthSectionId, SectionLayoutItem> {
    return buildLayoutMapFromEditorDrafts(
      this.editorItems(),
      this.SECTIONS as readonly { id: CoolCreditGrowthSectionId }[],
      this.DEFAULT_COL_SPAN,
      (span) => this._normalizeColSpan(span),
    );
  }

  private _syncLayoutFromEditor(): void {
    if (!this.isLayoutEditorOpen()) return;
    this.layoutMap.set(this._buildLayoutFromEditor());
  }

  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<CoolCreditGrowthSectionId, SectionLayoutItem> =
      {} as Record<CoolCreditGrowthSectionId, SectionLayoutItem>;
    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id as CoolCreditGrowthSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible !== false,
        colSpan: this._normalizeColSpan(storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 6),
        order: Number.isFinite(storedItem?.order) ? Number(storedItem!.order) : idx,
      };
    });
    if (!Object.values(layout).some((s) => s.visible)) {
      this.SECTIONS.forEach((s, idx) => {
        const sid = s.id as CoolCreditGrowthSectionId;
        layout[sid] = {
          id: sid,
          visible: true,
          colSpan: this._normalizeColSpan(this.DEFAULT_COL_SPAN[sid] ?? 6),
          order: idx,
        };
      });
      this._saveLayoutToStorage(layout);
    }
    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      const hasKnown = this.SECTIONS.some((s) => parsed[s.id] != null);
      return hasKnown ? parsed : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(layout: Record<CoolCreditGrowthSectionId, SectionLayoutItem>): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // ignore
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  toggleInsights() {
    this.showInsightsModal = !this.showInsightsModal;
  }

  closeInsightsModal() {
    this.showInsightsModal = false;
  }

}
